import React from "react";
import { TemplateProps, createTemplateUtils } from "../BaseTemplate";
import BasicInfoSection from "../../offer-wizard/BasicInfoSection";
import PublicPlanSummary from "../PublicPlanSummary";
import PublicFinancialSummary from "../PublicFinancialSummary";
import { analyticsService } from "../../../services/analyticsService";
import { getFormattedOfferValidityDate } from "../utils/offerUtils";

const ClassicTemplate: React.FC<TemplateProps> = ({
  offer,
  brandKit,
  termsAndConditions,
  editMode = false,
  onColorChange,
  showColorPicker,
  setShowColorPicker,
  onHeroUpload,
  onHeroRemove,
  uploadingHero = false,
  onTermsToggle,
  onTermsContentChange,
}) => {
  const utils = createTemplateUtils(brandKit);

  const handleClick = (e: React.MouseEvent) => {
    if (!editMode && analyticsService.isSessionActive()) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      analyticsService.trackClick(
        (e.target as HTMLElement).tagName.toLowerCase(),
        x,
        y,
        "offer-page"
      );
    }
  };

  return (
    <div
      className="font-serif"
      onClick={handleClick}
      style={
        {
          "--primary-color": utils.colors.primary || "#8B4513",
          "--secondary-color": utils.colors.secondary || "#D2691E",
          "--accent-color": utils.colors.accent || "#DAA520",
        } as React.CSSProperties
      }
    >
      <div className="max-w-4xl mx-auto bg-white">
        {/* Key Visual Section */}
        {(utils.keyVisualUrl || editMode) && (
          <div className="relative group mb-8">
            {utils.keyVisualUrl ? (
              <div className="relative">
                <img
                  src={utils.keyVisualUrl}
                  alt="Key Visual"
                  className="w-full h-64 object-cover border-4 border-amber-100"
                />
                {editMode && (
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-black group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                    <div className="hidden group-hover:flex space-x-4">
                      <button
                        onClick={() => {
                          const input = document.createElement("input");
                          input.type = "file";
                          input.accept = "image/jpeg,image/png,image/webp";
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement)
                              .files?.[0];
                            if (file) onHeroUpload?.(file);
                          };
                          input.click();
                        }}
                        className="px-4 py-2 bg-white text-amber-900 rounded hover:bg-amber-50 transition-colors font-medium"
                        disabled={uploadingHero}
                      >
                        {uploadingHero ? "Uploading..." : "Podmień"}
                      </button>
                      <button
                        onClick={onHeroRemove}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors font-medium"
                      >
                        Usuń
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : editMode ? (
              <div className="h-32 border-4 border-dashed border-amber-300 flex items-center justify-center">
                <button
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/jpeg,image/png,image/webp";
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) onHeroUpload?.(file);
                    };
                    input.click();
                  }}
                  className="px-6 py-3 bg-amber-700 text-white rounded hover:bg-amber-800 transition-colors font-medium"
                  disabled={uploadingHero}
                >
                  {uploadingHero ? "Uploading..." : "Dodaj Key Visual"}
                </button>
              </div>
            ) : null}
          </div>
        )}

        {/* Classic Header with ornamental borders */}
        <div className="border-4 border-double border-amber-400 mb-8 bg-white">
          <div className="border-2 border-amber-200 m-2">
            <div
              className="text-white py-8 px-8 text-center relative"
              style={{
                backgroundColor: utils.colors.primary || "#8B4513",
              }}
            >
              {/* Ornamental corners */}
              <div className="absolute top-2 left-2 w-8 h-8 opacity-30">
                <svg viewBox="0 0 40 40" className="w-full h-full">
                  <path
                    d="M5,5 L35,5 L35,15 L25,15 L25,25 L15,25 L15,35 L5,35 Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <div className="absolute top-2 right-2 w-8 h-8 opacity-30 transform rotate-90">
                <svg viewBox="0 0 40 40" className="w-full h-full">
                  <path
                    d="M5,5 L35,5 L35,15 L25,15 L25,25 L15,25 L15,35 L5,35 Z"
                    fill="currentColor"
                  />
                </svg>
              </div>

              <div className="relative z-10">
                <h1 className="text-4xl font-bold mb-4 tracking-wide">
                  {offer.title}
                </h1>
                <div className="w-24 h-1 bg-amber-300 mx-auto mb-4"></div>
                <p className="text-xl italic opacity-90">
                  Przygotowane specjalnie dla {offer.clientName}
                </p>
                <div className="mt-6 text-sm tracking-widest">
                  OFERTA NR {offer.id.slice(-8).toUpperCase()}
                </div>
              </div>

              {/* Bottom ornamental corners */}
              <div className="absolute bottom-2 left-2 w-8 h-8 opacity-30 transform rotate-180">
                <svg viewBox="0 0 40 40" className="w-full h-full">
                  <path
                    d="M5,5 L35,5 L35,15 L25,15 L25,25 L15,25 L15,35 L5,35 Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <div className="absolute bottom-2 right-2 w-8 h-8 opacity-30 transform rotate-270">
                <svg viewBox="0 0 40 40" className="w-full h-full">
                  <path
                    d="M5,5 L35,5 L35,15 L25,15 L25,25 L15,25 L15,35 L5,35 Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
            </div>

            {/* Hotel Info Banner */}
            <div
              className="px-8 py-4 text-center border-t-2"
              style={{
                backgroundColor: utils.colors.accent
                  ? `${utils.colors.accent}20`
                  : "#DAA52020",
                borderColor: utils.colors.accent || "#DAA520",
              }}
            >
              <div className="flex justify-between items-center">
                <div className="font-semibold text-amber-900 text-lg">
                  {offer.hotel.name}
                </div>
                <div className="text-amber-700 font-medium">
                  {utils.formatDate(new Date().toISOString())}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Sections with classic styling */}
        <div className="space-y-8 mb-8 px-4">
          <div className="border-l-4 border-amber-400 pl-6">
            <BasicInfoSection
              data={{
                title: offer.title,
                clientName: offer.clientName,
                clientEmail: offer.clientEmail,
                clientPhone: offer.clientPhone,
                description: offer.description || "",
                checkInDate: offer.checkInDate,
                checkOutDate: offer.checkOutDate,
                numberOfGuests: offer.numberOfGuests,
                numberOfRooms: offer.numberOfRooms,
                offerDays: offer.offerDays,
                currentDay: 1,
                netValue: offer.netValue,
              }}
              mode="public"
            />
          </div>
          <div className="border-l-4 border-amber-400 pl-6">
            <PublicPlanSummary offer={offer} />
          </div>
          <div className="border-l-4 border-amber-400 pl-6">
            {offer.financialSummary && (
              <PublicFinancialSummary
                financialSummary={offer.financialSummary}
              />
            )}
          </div>
        </div>

        {/* Terms and Conditions Section */}
        {termsAndConditions?.enabled ? (
          <div className="relative group border-4 border-double border-amber-300 p-6 mb-8 bg-amber-50">
            {editMode && (
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => onTermsToggle?.(false)}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                >
                  Usuń
                </button>
              </div>
            )}

            <h3 className="text-2xl font-bold mb-4 text-amber-900 text-center">
              ~ Warunki Oferty ~
            </h3>

            {editMode ? (
              <textarea
                value={termsAndConditions.content || ""}
                onChange={(e) => onTermsContentChange?.(e.target.value)}
                placeholder="Wprowadź warunki oferty..."
                className="w-full h-24 p-3 border-2 border-amber-300 rounded resize-none focus:ring-2 focus:ring-amber-500 focus:border-transparent font-serif"
              />
            ) : (
              <div
                className="text-amber-800 leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html:
                    termsAndConditions.content?.replace(/\n/g, "<br>") || "",
                }}
              />
            )}
          </div>
        ) : editMode ? (
          <div className="border-4 border-dashed border-amber-300 p-6 text-center mb-8">
            <button
              onClick={() => onTermsToggle?.(true)}
              className="px-6 py-3 bg-amber-700 text-white rounded hover:bg-amber-800 transition-colors font-medium"
            >
              Dodaj sekcję "Warunki oferty"
            </button>
          </div>
        ) : null}

        {/* Classic Call to Action */}
        <div className="border-4 border-double border-amber-400 p-8 mb-8 bg-gradient-to-b from-amber-50 to-white">
          <div className="text-center">
            <h3 className="text-3xl font-bold text-amber-900 mb-6">
              ⚜ Zapraszamy do Współpracy ⚜
            </h3>
            <p className="text-amber-800 text-lg mb-8 leading-relaxed italic">
              Będziemy zaszczyceni możliwością gościny i zapewnienia Państwu
              niezapomnianych chwil w naszym hotelu.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-6 mb-10">
              <button
                className="px-10 py-4 text-white rounded font-bold text-lg shadow-md hover:shadow-lg transition-all border-2 border-amber-600"
                style={{ backgroundColor: utils.colors.accent || "#DAA520" }}
              >
                ✓ Akceptuję Ofertę
              </button>
              <button className="px-10 py-4 bg-white border-2 border-amber-600 text-amber-800 rounded font-bold text-lg hover:bg-amber-50 transition-colors">
                ✉ Prośba o Modyfikację
              </button>
            </div>

            {/* Contact Information Card */}
            <div className="border-4 border-double border-amber-300 p-6 bg-white max-w-lg mx-auto">
              <h4 className="font-bold text-amber-900 text-xl mb-4 text-center">
                ~ {offer.hotel.name} ~
              </h4>

              {offer.hotel.address && (
                <p className="text-amber-800 mb-4 text-center italic">
                  {offer.hotel.address}
                </p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                {offer.hotel.phone && (
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-amber-600">☎</span>
                    <a
                      href={`tel:${offer.hotel.phone}`}
                      className="hover:opacity-80 font-medium"
                      style={{
                        color: utils.colors.secondary || "#D2691E",
                      }}
                    >
                      {offer.hotel.phone}
                    </a>
                  </div>
                )}
                {offer.hotel.email && (
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-amber-600">✉</span>
                    <a
                      href={`mailto:${offer.hotel.email}`}
                      className="hover:opacity-80 font-medium"
                      style={{
                        color: utils.colors.secondary || "#D2691E",
                      }}
                    >
                      {offer.hotel.email}
                    </a>
                  </div>
                )}
              </div>

              {offer.hotel.website && (
                <div className="mt-4 pt-4 border-t-2 border-amber-200 text-center">
                  <a
                    href={offer.hotel.website}
                    className="hover:opacity-80 font-medium text-sm"
                    style={{
                      color: utils.colors.secondary || "#D2691E",
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    🌐 {offer.hotel.website}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Classic Footer */}
        <footer className="text-center border-t-4 border-double border-amber-400 pt-6 mt-12">
          <div className="text-amber-700 font-medium">
            <p className="mb-2">
              ⚜ Ta oferta zachowuje ważność do{" "}
              {getFormattedOfferValidityDate(offer, utils)} ⚜
            </p>
            <p className="text-sm opacity-75">
              Dziękujemy za zainteresowanie naszymi usługami
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ClassicTemplate;
